import { Box, Grid, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { CustomButton } from "../../../../common/components/functional/customButton/CustomButton";
import InfoTooltip from "../../../../common/components/functional/InfoTooltip/InfoTooltip";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { RoundedButton } from "../../../../common/components/styled/styled";
import { isoFormat } from "../../../../common/helpers/formatDateTime";
import { ReactComponent as ArrowLeft } from "../../../../common/icons/arrowLeft.svg";
import { AutocompleteType } from "../../../../common/types/autoComplete";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import useIsLoading from "../../../../hooks/useIsLoading";
import { fetchCustomerOrgById } from "../../../customer/customerOrganizationSlice";
import { fetchWorkLocationsAndContractsByOrgId } from "../../../customer/customerWorkLocationsSlice";
import {
  fetchOrderTemplates,
  selectActiveOrderTemplates,
} from "../../../customer/orderTemplatesSlice";
import { fetchGetJobAdIdByBundleId } from "../../../jobAds/jobAdSlice";
import { fetchOrderQueueById } from "../../../settings/settingsActions";
import OrderBundleComments from "../../comments/OrderBundleComments";
import { CANCEL_LONG_ORDER_OPTIONS } from "../../constants";
import { OrderCancellationDialog } from "../../dialogs/OrderCancellationDialog";
import { OrderSaveDialog } from "../../dialogs/OrderSaveDialog";
import { LongOrderForm } from "../../newOrder/long/LongOrderForm";
import {
  OrderEdit,
  OrderHandler,
  OrderType,
  UpdateLongOrderRowBundle,
} from "../../orderAPI";
import {
  cancelRowBundle,
  saveLongOrderRow,
  updateTargetedShiftTargetingRules,
} from "../../orderRowsDetailedSlice";
import OrdersHeader from "../../OrdersHeader";
import { shortOrderDefaultFieldValues, WorkLocation } from "../../orderTypes";
import { clearTalentsStatus } from "../../talentsStatusSlice";
import { FormValueTypes } from "../../types/longOrderTypes";
import { CancelReasonBanner } from "../CancelReasonBanner";
import { EditReasonBanner } from "../EditReasonBanner";
import { OrderHandlerSelection } from "../OrderHandlerSelection";
import { StatusBanner } from "../StatusBanner";
import SearchOtherTalentsButton from "./talentsStatus/SearchOtherTalentsButton";
import { TalentsStatus } from "./talentsStatus/TalentsStatus";
import { toTargetingRule } from "../../../talentProfile/Employment/EmploymentAdditionalInfo/targetedShifts/TargetedShiftsForm";
import { toFormFieldRules } from "../../utils";

type Props = {
  order: OrderEdit;
};

export const EditLongOrder = ({ order }: Props) => {
  const { t } = useTranslation("order");
  const navigate = useNavigate();
  const isLoading = useIsLoading([
    "orderTemplates",
    "customerWorkLocations",
    "orderRowsDetailed",
  ]);
  const dispatch = useAppDispatch();
  const localized = useAppSelector((state) => state.localized);

  const [jobAdId, setJobAdId] = useState<string | null>(null);

  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_: SyntheticEvent, newTabIndex: number) => {
    setTabIndex(newTabIndex);
  };

  const tabs = [
    { label: t("order"), value: 0 },
    { label: t("orderTalentState"), value: 1 },
  ];

  const [workLocation, setWorkLocation] = useState<WorkLocation | null>();
  const [orgCCs, setOrgCCs] = useState<AutocompleteType[]>([]);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isTalentSearchOpen, setIsTalentSearchOpen] = useState(false);

  const [orderHandlerOptions, setOrderHandlerOptions] = useState<
    OrderHandler[]
  >([]);

  const hasInactiveWorkLocation = useMemo(
    () => !workLocation?.active,
    [workLocation],
  );
  const hasInactiveOrderRowTemplate = !useAppSelector(
    selectActiveOrderTemplates,
  ).find((template) => template.id === order?.orderRowTemplateId);
  const hasInactiveCustomerCCs = useMemo(() => {
    return order.customerCostCenterIds.some(
      (id) => !workLocation?.customerCCs.find((cc) => cc.id === id)?.active,
    );
  }, [order.customerCostCenterIds, workLocation]);

  const canCopy =
    !hasInactiveWorkLocation &&
    !hasInactiveOrderRowTemplate &&
    !hasInactiveCustomerCCs;

  const cannotCopyOrderTooltip = () =>
    [
      `${t("order:cannotCopyOrder")}:`,
      hasInactiveWorkLocation && `• ${t("order:inactiveWorkLocation")}`,
      hasInactiveOrderRowTemplate && `• ${t("order:inactiveOrderRowTemplate")}`,
      hasInactiveCustomerCCs && `• ${t("order:inactiveCustomerCostCenters")}`,
    ]
      .filter(Boolean)
      .join("\n");

  useEffect(() => {
    return () => {
      dispatch(clearTalentsStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (order?.contractId) {
      dispatch(fetchOrderTemplates({ contractId: order.contractId }));
    }
  }, [order.contractId, dispatch]);

  useEffect(() => {
    // Fetch GetJobAdByBundleId to see if job ad exists for the order
    dispatch(fetchGetJobAdIdByBundleId(order.bundleId)).then((action) => {
      if (isFulfilled(action)) {
        setJobAdId(action.payload.jobAdId);
      }
    });
  }, [dispatch, order.bundleId]);

  useEffect(() => {
    if (!order.orderQueueId) return;
    dispatch(fetchOrderQueueById(order.orderQueueId)).then((action) => {
      if (isFulfilled(action)) {
        const handlers = action.payload.members.map((handler) => ({
          id: handler.id,
          name: `${handler.firstName} ${handler.lastName}`,
        }));
        setOrderHandlerOptions(handlers);
      }
    });
  }, [order.status, order.orderQueueId, dispatch]);

  const sortedEditReasons =
    order.reasonsForEdits &&
    [...order.reasonsForEdits].sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    );

  const defaultValues: FormValueTypes = useMemo(() => {
    const custCostCenters = workLocation?.customerCCs.length
      ? workLocation?.customerCCs
      : orgCCs;
    return {
      edit: true,
      uid: order.bundleId,
      isReadOnly: order.status.isReadOnly,
      title: order.title,
      customerOrgId: order.customerOrgId,
      type: order.type as OrderType,
      workLocation: {
        id: order.workLocationId,
        name: order.workLocationName,
      },
      orderTemplate: {
        ...shortOrderDefaultFieldValues.orderTemplate,
        id: order?.orderRowTemplateId,
        name: order?.orderRowTemplateName,
        tesId: order?.taskSpec.tesId,
        workTitleId: order?.taskSpec.workTitleId,
      },
      amount: order.amount,
      customerCostCenters: order?.customerCostCenterIds?.map((id) => ({
        customerCCId: id,
        id,
        name: custCostCenters.find((CC) => CC.id === id)?.name || "",
      })),
      reasonForTemporaryEmployment: order.reasonForTemporaryEmployment,
      workStarts: new Date(order.workStarts),
      workEnds: order.workEnds ? new Date(order.workEnds) : null,
      shortWorkTimeDescriptionFi: order.shortWorkTimeDescriptionFi || "",
      shortWorkTimeDescriptionEn: order.shortWorkTimeDescriptionEn || "",
      longWorkTimeDescriptionFi: order.longWorkTimeDescriptionFi || "",
      longWorkTimeDescriptionEn: order.longWorkTimeDescriptionEn || "",
      workDescriptionFi: order.taskSpec.description || "",
      workDescriptionEn: order.taskSpec.descriptionEn || "",
      arrivalInstructionsFi: order.taskSpec.descriptionForTalent || "",
      arrivalInstructionsEn: order.taskSpec.descriptionForTalentEn || "",
      languages: order.taskSpec.languageSkills,
      certificates: order.taskSpec.certificateIds.map((id) => ({
        id,
        name: localized.certificate[id],
      })),
      skills: order.taskSpec.skillIds.map((id) => ({
        id,
        name: localized.skill[id],
      })),
      trainings: order.taskSpec.trainings.map((id) => ({
        id,
        name: localized.training[id],
      })),
      tesVariation: order.taskSpec.tesVariation,
      descriptionToEezyAndCustomer: order.descriptionToEezyAndCustomer || "",
      targetedShiftTargetingRules: toFormFieldRules(
        order?.targetedShiftTargetingRules,
        localized,
      ),
    };
  }, [order, localized, workLocation, orgCCs]);

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });
  const {
    getValues,
    reset,
    formState: { isDirty },
  } = methods;

  const handleSubmit = (editReason: string) => {
    const {
      reasonForTemporaryEmployment,
      workStarts,
      workEnds,
      customerCostCenters,
      amount,
      shortWorkTimeDescriptionFi,
      shortWorkTimeDescriptionEn,
      longWorkTimeDescriptionFi,
      longWorkTimeDescriptionEn,
      languages,
      skills,
      certificates,
      trainings,
      tesVariation,
      workDescriptionFi,
      workDescriptionEn,
      arrivalInstructionsFi,
      arrivalInstructionsEn,
      descriptionToEezyAndCustomer,
      targetedShiftTargetingRules,
    } = getValues();
    const payload: UpdateLongOrderRowBundle = {
      bundleId: order.bundleId,
      version: order.version,
      type: order.type as OrderType,
      startDate: format(workStarts!!, isoFormat),
      ...(order.type === OrderType.FixedTerm && {
        reasonForTemporaryEmployment,
        endDate: format(workEnds!!, isoFormat),
      }),
      customerCostCenterIds: customerCostCenters.map(
        (cc) => cc.customerCCId || cc.id,
      ),
      requiredTalentAmount: amount,
      tesVariation,
      shortWorkTimeDescriptionFi,
      shortWorkTimeDescriptionEn,
      longWorkTimeDescriptionFi,
      longWorkTimeDescriptionEn,
      talentRequirements: {
        languages,
        skills: skills.map((skill) => skill.id),
        certificates: certificates.map((cert) => cert.id),
        trainings: trainings.map((training) => training.id),
      },
      workDescriptionFi,
      workDescriptionEn,
      arrivalInstructionsFi,
      arrivalInstructionsEn,
      descriptionToEezyAndCustomer,
      reasonForChange: editReason,
      targetedShiftTargetingRules: targetedShiftTargetingRules.map((rule) =>
        toTargetingRule(rule),
      ),
    };
    dispatch(saveLongOrderRow(payload)).then((action) => {
      if (isFulfilled(action)) {
        // Update to redux state
        const rules = targetedShiftTargetingRules;
        dispatch(
          updateTargetedShiftTargetingRules({
            bundleId: order.bundleId,
            rules,
          }),
        );
        reset(getValues());
      }
    });
  };

  const handleSave = (editReason: string) => {
    handleSubmit(editReason);
    setIsEditDialogOpen(false);
  };

  const handleCancel = (reasonForCancellation: string) => {
    const data = {
      bundleId: order.bundleId,
      reasonForCancellation,
    };
    dispatch(cancelRowBundle(data)).then((action: any) => {
      if (isFulfilled(action)) {
        navigate("/home/orders");
      }
    });
    setIsCancelDialogOpen(false);
  };

  const handleCopyOrder = () => {
    navigate(`/home/orders/${order.bundleId}/copy`);
  };

  // Get customer cost center names for the order
  useEffect(() => {
    if (!order.customerOrgId) return;
    dispatch(
      fetchWorkLocationsAndContractsByOrgId({ id: order.customerOrgId }),
    ).then((action) => {
      if (isFulfilled(action)) {
        const workLocations = action.payload.workLocations;
        const workLocation = workLocations.find(
          (wl) => wl.id === order.workLocationId,
        ) as WorkLocation;
        setWorkLocation(workLocation);
        if (!workLocation.customerCCs.length) {
          dispatch(fetchCustomerOrgById({ id: order.customerOrgId })).then(
            (action) => {
              if (isFulfilled(action)) {
                setOrgCCs(action.payload.costCenters);
              }
            },
          );
        }
      }
    });
  }, [order.customerOrgId, order.workLocationId, dispatch]);

  // Update default values after cc names have been fetched
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <>
      <CancelReasonBanner reasonText={order.cancelReason} />
      <StatusBanner mainStatus={order.status.mainStatus} />
      <Box sx={{ flexGrow: 1, maxWidth: "2000px", overflowX: "auto" }}>
        <OverlaySpinner
          sx={{ width: "100%", height: "100%" }}
          isLoading={isLoading}
        />
        <Grid container item xs={12} justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <IconButton onClick={() => navigate("/home/orders")}>
              <ArrowLeft style={{ width: "24px", height: "24px" }} />
            </IconButton>
            <OrdersHeader
              title={order.title || order.orderRowTemplateName}
              customerCompany={order.customerOrgName}
              orderRowCount={order.orderRows.length}
            />
            <RoundedButton
              variant="outlined"
              sx={{ height: "36px", margin: "auto 0" }}
              onClick={() =>
                jobAdId
                  ? navigate(`/home/jobAds/${jobAdId}`)
                  : navigate(`/home/jobAds/new?order=${order.bundleId}`)
              }
              disabled={order.status.isReadOnly}
            >
              {jobAdId ? t("order:viewJobAd") : t("order:createJobAd")}
            </RoundedButton>
          </Box>
          <Box ml={2} display="flex" alignItems="center">
            <>
              {!isLoading && !canCopy && (
                <InfoTooltip
                  style={{ marginRight: "8px", height: "24px" }}
                  title={cannotCopyOrderTooltip()}
                  variant="warning"
                  slotProps={{ popper: { sx: { whiteSpace: "pre-line" } } }}
                />
              )}
              <CustomButton
                variant="outlined"
                sx={{ borderRadius: "18px", marginRight: "20px" }}
                disabled={!canCopy}
                onClick={handleCopyOrder}
              >
                {t("order:copyOrder")}
              </CustomButton>
            </>

            <OrderHandlerSelection
              bundleId={order.bundleId}
              status={order.status}
              handlers={order.orderHandlers}
              handlerOptions={orderHandlerOptions}
              isLongOrder
            />
          </Box>
        </Grid>
        <Grid
          container
          columnSpacing={2}
          rowSpacing={2}
          sx={{ flexWrap: "nowrap" }}
        >
          <Grid item sx={{ flex: "1 1 auto", minWidth: "600px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                verticalAlign: "middle",
              }}
            >
              <Tabs
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "capitalize",
                    fontWeight: 700,
                  },
                }}
                value={tabIndex}
                onChange={handleTabChange}
              >
                {tabs.map((tab) => (
                  <Tab
                    key={tab.label}
                    value={tab.value}
                    label={
                      <Typography color="textSecondary" variant="header">
                        {tab.label}
                      </Typography>
                    }
                  />
                ))}
              </Tabs>
              {tabIndex === 1 && (
                <SearchOtherTalentsButton
                  label={t("searchOtherTalents")}
                  disabled={order.status.isReadOnly}
                  onClick={() => setIsTalentSearchOpen(true)}
                />
              )}
            </Box>
            {tabIndex === 0 && (
              <>
                {/* TODO: componize */}
                {order.reasonsForEdits ? (
                  <>
                    {sortedEditReasons?.map((reason, i) => (
                      <EditReasonBanner key={i} reason={reason} />
                    ))}
                  </>
                ) : null}
                <FormProvider {...methods}>
                  <UnsavedChangesProvider isDirty={isDirty}>
                    <LongOrderForm
                      saveAction={() => setIsEditDialogOpen(true)}
                      cancelAction={() => setIsCancelDialogOpen(true)}
                    />
                  </UnsavedChangesProvider>
                  <OrderSaveDialog
                    isOpen={isEditDialogOpen}
                    closeDialog={() => setIsEditDialogOpen(false)}
                    finishSave={handleSave}
                    isLoading={isLoading}
                  />
                  <OrderCancellationDialog
                    isOpen={isCancelDialogOpen}
                    closeDialog={() => setIsCancelDialogOpen(false)}
                    onCancel={handleCancel}
                    cancelOptions={CANCEL_LONG_ORDER_OPTIONS}
                    isLoading={isLoading}
                    disableTalentReplacement
                  />
                </FormProvider>
              </>
            )}
            {tabIndex === 1 && (
              <TalentsStatus
                bundleId={order.bundleId}
                isReadOnly={order.status.isReadOnly}
                order={order}
                jobAdId={jobAdId}
                searchDialogOpen={isTalentSearchOpen}
                onSearchDialogClose={() => setIsTalentSearchOpen(false)}
              />
            )}
          </Grid>
          <Grid item sx={{ flex: "0 0 560px", marginTop: "48px" }}>
            <OrderBundleComments bundleId={order.bundleId} />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
